import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AdditionalInfoForm,
  BusinessProfileStep,
  BusinessAboutStep,
  BusinessRolesStep,
  BusinessAccountSetup,
  CreatorAccountSetup,
  BusinessStepIndicator,
  createBusinessFormData,
  toBusinessProfilePayload,
  uploadBusinessProfileImage,
  BaseButton,
  Chip,
  reissueToken,
  saveBusinessProfile,
  saveUserGoal,
  useAuth,
  useIsMobile,
  useT,
} from '@repo/common';
import { ReactComponent as KoLogoXS } from '@repo/common/assets/logos/koready/xs/logo-light.svg';
import { ReactComponent as OingLogoXS } from '@repo/common/assets/logos/xs/logo-black.svg';
import { IS_LEGACY_DOMAIN } from '../../utils/brand.js';
import {
  BUSINESS_PROFILE_STORAGE_KEY,
} from '../../constants/businessProfile';

const LogoXS = IS_LEGACY_DOMAIN ? OingLogoXS : KoLogoXS;

const USER_STEP_1_CATEGORIES = [
  { label: 'Learn Korean', labelKr: '한국어 배우기' },
  { label: 'Find jobs in Korea', labelKr: '한국에서 일자리 찾기' },
  { label: 'Prepare documents', labelKr: '서류 준비하기' },
  { label: 'Join workshops', labelKr: '워크숍 참여하기' },
  { label: 'Explore Korea life', labelKr: '한국 생활 탐색하기' },
  { label: 'Other', labelKr: '기타' },
];

const StepIndicator = ({ currentStep, totalSteps, maxStep, onStepSelect }) => {
  const t = useT();
  return (
    <div className='flex items-center gap-2'>
      <div className='flex items-center gap-1'>
        {Array.from({ length: totalSteps }).map((_, i) => {
          const stepNumber = i + 1;
          const canSelect = Boolean(onStepSelect) && stepNumber <= maxStep;

          return (
            <React.Fragment key={i}>
              <button
                type='button'
                disabled={!canSelect}
                aria-label={`Step ${stepNumber}`}
                aria-current={stepNumber === currentStep ? 'step' : undefined}
                onClick={() => onStepSelect?.(stepNumber)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  canSelect
                    ? 'cursor-pointer hover:scale-125'
                    : 'cursor-default'
                } ${
                  i < currentStep - 1
                    ? 'bg-green-deep04'
                    : i === currentStep - 1
                      ? 'bg-green-deep02'
                      : 'bg-grayscale-gray03'
                }`}
              />
              {i < totalSteps - 1 && (
                <div
                  className={`w-6 h-[2px] transition-colors duration-300 ${
                    i < currentStep - 1
                      ? 'bg-green-deep02'
                      : 'bg-grayscale-gray03'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      <span className='typo-b12Strong text-text-secondary'>
        {t('Step', '단계')} {currentStep} / {totalSteps}
      </span>
    </div>
  );
};

const GoalStep = ({ onComplete, onSkip, isMobile, initialSelected = null }) => {
  const t = useT();
  const [selected, setSelected] = useState(initialSelected);
  const toggleGoal = (goal) =>
    setSelected((prev) => (prev === goal ? null : goal));

  return (
    <div className='lg:bg-grayscale-gray00 lg:rounded-2xl lg:p-12 lg:pb-10 w-full max-w-[520px] flex flex-col gap-[40px] lg:gap-8 lg:shadow-[0_4px_24px_0_rgba(0,0,0,0.06)]'>
      <div className='flex flex-col gap-2'>
        <div className='typo-title1 text-text-primary'>
          {t("What's your main goal?", '주요 목표가 무엇인가요?')}
        </div>
        <div className='typo-b14 text-text-secondary'>
          {t(
            'Select a goal to personalize your experience',
            '맞춤형 경험을 위해 목표를 선택해 주세요',
          )}
        </div>
      </div>
      <div className='flex flex-wrap gap-2'>
        {USER_STEP_1_CATEGORIES.map((item) => (
          <Chip
            key={item.label}
            size={isMobile ? 'l' : 'm'}
            type={selected === item.label ? 'fill' : 'outline'}
            state={selected === item.label ? 'selected' : 'default'}
            hideIcon
            onClick={() => toggleGoal(item.label)}
            bgclass={
              selected === item.label
                ? ''
                : 'bg-grayscale-gray01 lg:bg-grayscale-gray00'
            }
          >
            {t(item.label, item.labelKr)}
          </Chip>
        ))}
      </div>
      <StepFooter>
        <BaseButton
          type='primary'
          size='l'
          hideIcon
          onClick={() => onComplete(selected)}
          width='100%'
        >
          {t('Save and continue', '저장하고 계속하기')}
        </BaseButton>
        <button
          onClick={onSkip}
          className='underline px-3 py-2.5 text-grayscale-gray07 typo-b14Strong'
          bgclass='border-none'
        >
          {t('Skip for now', '나중에 하기')}
        </button>
      </StepFooter>
    </div>
  );
};

const StepFooter = ({ children }) => (
  <div
    className='fixed bottom-0 left-0 right-0 z-20 flex flex-col items-center gap-1 w-full
               px-[30px] pt-4 pb-5 bg-grayscale-gray01
               lg:static lg:z-auto lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0'
  >
    {children}
  </div>
);

const AdditionalInfo = () => {
  const location = useLocation();
  const fromSettings = location.state?.entry === 'settings';
  const isMobile = useIsMobile();
  const role =
    location.state?.role || sessionStorage.getItem('auth:role') || 'U';
  const returnTo = location.state?.returnTo || '/';

  const isCreator = location.state?.isCreator ?? false;
  const isBusiness = ['E', 'ROLE_E', 'EMPLOYER', 'ROLE_EMPLOYER'].includes(
    String(role).toUpperCase(),
  );
  const totalSteps = isBusiness ? 3 : 2;
  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);
  const [creatorData, setCreatorData] = useState({
    categories: [],
    portfolio: '',
    goal: null,
    bank: '',
    accountNumber: '',
  });
  const [businessData, setBusinessData] = useState(() => {
    let storedData = {};
    try {
      storedData = JSON.parse(
        sessionStorage.getItem('auth:registrationData') ||
          localStorage.getItem('auth:businessRegistrationData') ||
          '{}',
      );
    } catch (error) {
      console.error('Failed to restore business registration data:', error);
    }

    const socialUser = location.state?.socialUserData;
    const contactName = (
      location.state?.contactName ||
      storedData.contactName ||
      ''
    ).trim();
    // 가입 폼의 담당자 이름은 한 칸이라 첫 단어를 이름, 나머지를 성으로 나눈다
    const [contactFirst = '', ...contactRest] = contactName.split(/\s+/);

    return createBusinessFormData({
      companyName: location.state?.companyName || storedData.companyName || '',
      firstName: contactName
        ? contactFirst
        : location.state?.firstName || socialUser?.firstName || '',
      lastName: contactName
        ? contactRest.join(' ')
        : location.state?.lastName || socialUser?.lastName || '',
      profilePreview: socialUser?.profileUrl || '',
    });
  });

  const goToStep = (next) => {
    setStep(next);
    setMaxStep((prev) => Math.max(prev, next));
  };

  const handleSkip = () => {
    window.location.href = returnTo;
  };

  const t = useT();
  const { refreshUser } = useAuth();
  const [converting, setConverting] = useState(false);
  const convertingRef = useRef(false);
  const [convertError, setConvertError] = useState('');

  /*
   * 기업 계정은 가입 시점이 아니라 필수 입력 단계(step 1)에서 Continue 를 누를 때 만든다.
   * 전환에 필요한 필수 항목이 이 단계에서 모두 채워지기 때문이다.
   */
  const handleBusinessConvert = async () => {
    if (convertingRef.current) return;
    convertingRef.current = true;
    setConverting(true);
    setConvertError('');
    try {
      await saveBusinessProfile({
        ...toBusinessProfilePayload(businessData),
        convertAccount: true,
      });
      await reissueToken();
      await refreshUser();
      goToStep(2);
    } catch (error) {
      console.error('기업 계정 전환 실패:', error);
      setConvertError(
        t(
          'Could not create your business account. Please try again.',
          '비즈니스 계정을 만들지 못했어요. 다시 시도해 주세요.',
        ),
      );
    } finally {
      convertingRef.current = false;
      setConverting(false);
    }
  };

  const handleBusinessComplete = async () => {
    const payload = toBusinessProfilePayload(businessData);
    const serializedBusinessData = JSON.stringify({
      ...businessData,
      ...payload,
      profileFile: undefined,
    });

    /*
     * 브라우저 저장소는 화면 복귀용 캐시로만 남긴다.
     * 예전에는 여기가 유일한 저장소라 기기를 바꾸면 회사 정보가 통째로 사라졌다.
     */
    sessionStorage.setItem(
      BUSINESS_PROFILE_STORAGE_KEY,
      serializedBusinessData,
    );
    localStorage.setItem(BUSINESS_PROFILE_STORAGE_KEY, serializedBusinessData);

    try {
      await saveBusinessProfile(payload);
    } catch (error) {
      // 저장에 실패해도 온보딩을 붙잡아두지 않는다. 값은 캐시에 남아 있다
      console.error('기업 정보 저장 실패:', error);
    }

    try {
      await uploadBusinessProfileImage(businessData);
    } catch (error) {
      // 이미지는 마이페이지에서 다시 올릴 수 있으므로 온보딩을 막지 않는다
      console.error('기업 프로필 이미지 저장 실패:', error);
    }

    sessionStorage.removeItem('auth:registrationData');
    localStorage.removeItem('auth:businessRegistrationData');
    handleSkip();
  };

  const handleGoalComplete = async (goal) => {
    setCreatorData((d) => ({ ...d, goal }));
    try {
      await saveUserGoal({ goal });
    } catch (error) {
      console.error('goal 저장 실패:', error);
    } finally {
      handleSkip();
    }
  };

  const renderStep = () => {
    if (isBusiness) {
      if (step === 1) {
        return (
          <BusinessProfileStep
            data={businessData}
            onChange={setBusinessData}
            onComplete={handleBusinessConvert}
          />
        );
      }
      if (step === 2) {
        return (
          <BusinessAboutStep
            data={businessData}
            onChange={setBusinessData}
            onComplete={() => goToStep(3)}
            onSkip={() => goToStep(3)}
          />
        );
      }
      return (
        <BusinessRolesStep
          data={businessData}
          onChange={setBusinessData}
          onComplete={handleBusinessComplete}
          onSkip={handleBusinessComplete}
        />
      );
    }

    if (step === 1) {
      return (
        <AdditionalInfoForm
          onComplete={() => goToStep(2)}
          onSkip={handleSkip}
          isCreator={isCreator}
        />
      );
    }
    if (!isCreator && step === 2) {
      return (
        <GoalStep
          initialSelected={creatorData.goal}
          onComplete={handleGoalComplete}
          onSkip={handleSkip}
          isMobile={isMobile}
        />
      );
    }

  };

  if (fromSettings && isBusiness) {
    return <BusinessAccountSetup onExit={handleSkip} />;
  }

  if (isCreator && !isBusiness) {
    return <CreatorAccountSetup fromSettings={fromSettings} onExit={handleSkip} />;
  }

  const lockScroll = isBusiness && step !== 1;

  return (
    <div
      className={`${isBusiness ? 'business-mobile-page' : ''} bg-grayscale-gray01 w-full ${
        lockScroll ? 'h-[100dvh] overflow-hidden' : 'min-h-screen overflow-y-auto'
      }`}
    >
      <div
        className={`w-full flex flex-col items-center justify-start px-[30px] lg:px-[80px] py-[40px] gap-[40px] ${
          lockScroll ? 'h-full pb-40 lg:pb-[40px]' : 'min-h-screen'
        }`}
      >
        <div className='flex items-center justify-between w-full'>
          <Link to='/' aria-label='Home' className='hidden lg:block'>
            {isBusiness ? <KoLogoXS /> : <LogoXS />}
          </Link>
          {isBusiness ? (
            <BusinessStepIndicator
              currentStep={step}
              totalSteps={totalSteps}
              maxStep={maxStep}
              onStepSelect={goToStep}
            />
          ) : (
            <StepIndicator
              currentStep={step}
              totalSteps={totalSteps}
              isCreator={isCreator}
              maxStep={maxStep}
              onStepSelect={goToStep}
            />
          )}
        </div>

        <div
          className={`${isBusiness ? 'business-mobile-content flex-col gap-4' : ''} flex items-center justify-center w-full ${
            lockScroll ? 'flex-1 min-h-0' : 'lg:min-h-[800px] h-full'
          }`}
        >
          {isBusiness ? (
            <>
              {convertError && (
                <p role='alert' className='typo-b14Strong text-text-failed'>
                  {convertError}
                </p>
              )}
              {converting && (
                <p role='status' className='typo-b14 text-text-secondary'>
                  {t('Saving…', '저장 중…')}
                </p>
              )}
              <fieldset
                disabled={converting}
                className='w-full max-w-[520px] min-w-0 flex justify-center'
              >
                {renderStep()}
              </fieldset>
            </>
          ) : (
            renderStep()
          )}
        </div>
      </div>
    </div>
  );
};

export default AdditionalInfo;
